import moment from 'moment';
import { TurniDefaultInterface } from '../../../interfaces/default-turni-point-of-sale';

// Definizione dei tipi per maggiore type-safety
type TurniType = 'calendar' | 'shift';
type RangeNumber = 1 | 2;
type DayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';
type ErrorDefaultHoursMap = Record<string, boolean>;

/**
 * Controlla tutti gli orari predefiniti e restituisce un oggetto con gli errori
 * @param defaultHours - Configurazione degli orari predefiniti
 * @returns Oggetto che mappa ogni chiave di turno a un valore booleano (true = errore)
 */
export const checkAllDefaultHours = (
  defaultHours: TurniDefaultInterface
): ErrorDefaultHoursMap => {
  // Inizializziamo l'oggetto risultato con valori di default
  const errorDefaultHours: ErrorDefaultHoursMap = {};

  // Definiamo i giorni della settimana e i tipi di turno
  const days: DayOfWeek[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  const turniTypes: TurniType[] = ['calendar', 'shift'];

  // Generiamo dinamicamente tutte le chiavi di errore possibili
  for (const turniType of turniTypes) {
    for (const day of days) {
      for (const rangeNumber of [1, 2] as RangeNumber[]) {
        const key = `${turniType}${day}${rangeNumber}`;
        errorDefaultHours[key] = checkDefaultHoursValue(
          turniType,
          rangeNumber,
          day,
          defaultHours
        );
      }
    }
  }

  return errorDefaultHours;
};

/**
 * Verifica se un singolo intervallo orario ha errori
 * @param turniType - Tipo di turno ('calendar' o 'shift')
 * @param rangeNumber - Numero dell'intervallo (1 o 2)
 * @param day - Giorno della settimana
 * @param hours - Configurazione degli orari predefiniti
 * @returns true se c'è un errore, false se l'orario è valido
 */
export const checkDefaultHoursValue = (
  turniType: TurniType,
  rangeNumber: RangeNumber,
  day: string,
  hours?: TurniDefaultInterface
): boolean => {
  // Verifichiamo che hours sia definito
  if (!hours) return true;

  const range = hours[`${turniType}${day}`];

  // Se il range non è definito, non c'è un errore
  if (!range) return false;

  // Estraiamo i tempi di inizio e fine dell'intervallo corrente
  const rangeStart = range[`startTime${rangeNumber}`]
    ? moment(range[`startTime${rangeNumber}`].toDate())
    : null;

  const rangeEnd = range[`endTime${rangeNumber}`]
    ? moment(range[`endTime${rangeNumber}`].toDate())
    : null;

  // Verifichiamo che entrambi i valori esistano
  if (!rangeStart && !rangeEnd) return false;
  if ((rangeStart && !rangeEnd) || (!rangeStart && rangeEnd)) return true;

  // Verifichiamo che l'intervallo sia valido (inizio deve essere prima della fine)
  if (rangeStart.isSameOrAfter(rangeEnd)) return true;

  // Controlliamo la sovrapposizione con l'altro intervallo dello stesso tipo
  if (isOverlappingWithOtherRange(range, rangeNumber, rangeStart, rangeEnd)) {
    return true;
  }

  // Se è un turno di tipo 'shift', non facciamo ulteriori controlli
  if (turniType === 'shift') return false;

  // Per i turni di tipo 'calendar', controlliamo se sono contenuti nei turni 'shift'
  return !isCalendarWithinShift(hours, day, rangeStart, rangeEnd);
};

/**
 * Verifica se l'intervallo corrente si sovrappone con l'altro intervallo dello stesso turno
 * @param range - Configurazione del range da controllare
 * @param currentRangeNumber - Numero dell'intervallo corrente
 * @param currentStart - Orario di inizio dell'intervallo corrente
 * @param currentEnd - Orario di fine dell'intervallo corrente
 * @returns true se c'è sovrapposizione, false altrimenti
 */
function isOverlappingWithOtherRange(
  range: any,
  currentRangeNumber: RangeNumber,
  currentStart: moment.Moment,
  currentEnd: moment.Moment
): boolean {
  const otherRangeNumber = currentRangeNumber === 1 ? 2 : 1;

  const otherStart = range[`startTime${otherRangeNumber}`]
    ? moment(range[`startTime${otherRangeNumber}`].toDate())
    : null;

  const otherEnd = range[`endTime${otherRangeNumber}`]
    ? moment(range[`endTime${otherRangeNumber}`].toDate())
    : null;

  if (!otherStart || !otherEnd) return false;

  // Controlliamo se c'è sovrapposizione tra gli intervalli
  return (
    otherStart.isBetween(currentStart, currentEnd, 'minutes', '[)') ||
    otherEnd.isBetween(currentStart, currentEnd, 'minutes', '(]')
  );
}

/**
 * Verifica se un intervallo di calendario è contenuto in un intervallo di turno
 * @param hours - Configurazione degli orari predefiniti
 * @param day - Giorno della settimana
 * @param calendarStart - Orario di inizio dell'intervallo calendario
 * @param calendarEnd - Orario di fine dell'intervallo calendario
 * @returns true se l'intervallo calendario è contenuto in un intervallo turno, false altrimenti
 */
function isCalendarWithinShift(
  hours: TurniDefaultInterface,
  day: string,
  calendarStart: moment.Moment,
  calendarEnd: moment.Moment
): boolean {
  const shiftRange = hours[`shift${day}`];
  if (!shiftRange) return false; // Se non c'è turno definito, non c'è errore

  // Controlliamo entrambi gli intervalli di turno
  for (const rangeNumber of [1, 2] as RangeNumber[]) {
    const shiftStart = shiftRange[`startTime${rangeNumber}`]
      ? moment(shiftRange[`startTime${rangeNumber}`].toDate())
      : null;

    const shiftEnd = shiftRange[`endTime${rangeNumber}`]
      ? moment(shiftRange[`endTime${rangeNumber}`].toDate())
      : null;

    // Se entrambi i tempi del turno esistono e il calendario è contenuto nel turno
    if (
      shiftStart &&
      shiftEnd &&
      calendarStart.isSameOrAfter(shiftStart) &&
      calendarEnd.isSameOrBefore(shiftEnd)
    ) {
      return true;
    }
  }

  return false;
}
